import { FormEvent, useEffect, useRef, useState } from 'react'
import { eventsApi } from '../services/api'

const NAME_KEY = 'od-event-author-name'
const EMAIL_KEY = 'od-event-author-email'
const MAX_IMAGES = 6
const MAX_BYTES = 5 * 1024 * 1024

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4 9h11M7 7h.01M10 7h.01M12 14l7-7M15 7h4v4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18 14.5V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type Pick = { file: File; url: string }

// Social-share proof: image(s) + an optional note/link for the contest. This is
// distinct from the works leaderboard above and is gated per event.
export default function EventPrivateSubmit({ slug }: { slug: string }) {
  const [picks, setPicks] = useState<Pick[]>([])
  const [message, setMessage] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    try {
      const savedName = localStorage.getItem(NAME_KEY)
      if (savedName) setName(savedName)
      const savedEmail = localStorage.getItem(EMAIL_KEY)
      if (savedEmail) setEmail(savedEmail)
    } catch {
      /* ignore */
    }
  }, [])

  // Revoke object URLs on unmount to avoid leaks.
  useEffect(() => () => picks.forEach((p) => URL.revokeObjectURL(p.url)), [picks])

  function addFiles(list: FileList | null) {
    if (!list) return
    setError('')
    const incoming = Array.from(list)
    const next: Pick[] = []
    for (const file of incoming) {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed.')
        continue
      }
      if (file.size > MAX_BYTES) {
        setError('Each image must be under 5 MB.')
        continue
      }
      next.push({ file, url: URL.createObjectURL(file) })
    }
    setPicks((prev) => [...prev, ...next].slice(0, MAX_IMAGES))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function removeAt(idx: number) {
    setPicks((prev) => {
      const p = prev[idx]
      if (p) URL.revokeObjectURL(p.url)
      return prev.filter((_, i) => i !== idx)
    })
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (picks.length === 0) {
      setError('Add at least one image.')
      return
    }
    const trimmedEmail = email.trim()
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email, or leave it blank.')
      return
    }

    setSubmitting(true)
    try {
      await eventsApi.submitPrivate(slug, {
        message: message.trim(),
        author_name: name.trim(),
        email: trimmedEmail,
        images: picks.map((p) => p.file),
      })
      try {
        if (name.trim()) localStorage.setItem(NAME_KEY, name.trim())
        if (trimmedEmail) localStorage.setItem(EMAIL_KEY, trimmedEmail)
      } catch {
        /* ignore */
      }
      picks.forEach((p) => URL.revokeObjectURL(p.url))
      setPicks([])
      setMessage('')
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your submission.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="od-event-private" aria-label="Social share submission">
      <div className="od-event-private-head">
        <h2>
          <ShareIcon />
          Share your website
        </h2>
        <p className="od-event-private-sub">
          Post your website on social media, then send us a screenshot or link here. The winning
          share gets a Stanley coffee cup. We will contact you by email if you win.
        </p>
      </div>

      {done ? (
        <div className="od-event-private-done">
          <strong>Thanks — got it.</strong>
          <p>Your share was submitted for the Stanley coffee cup contest.</p>
          <button type="button" className="od-event-private-again" onClick={() => setDone(false)}>
            Submit another
          </button>
        </div>
      ) : (
        <form className="od-event-private-form" onSubmit={handleSubmit}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            hidden
            onChange={(e) => addFiles(e.target.files)}
          />

          <div className="od-event-private-thumbs">
            {picks.map((p, i) => (
              <div key={i} className="od-event-private-thumb">
                <img src={p.url} alt="" />
                <button
                  type="button"
                  aria-label="Remove image"
                  className="od-event-private-thumb-x"
                  onClick={() => removeAt(i)}
                >
                  ×
                </button>
              </div>
            ))}
            {picks.length < MAX_IMAGES && (
              <button
                type="button"
                className="od-event-private-add"
                onClick={() => fileInputRef.current?.click()}
              >
                <span aria-hidden="true">+</span>
                Add image
              </button>
            )}
          </div>
          <span className="od-event-private-hint">
            Up to {MAX_IMAGES} images · 5&nbsp;MB each · PNG, JPEG, WebP or GIF
          </span>

          <textarea
            className="od-event-textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            placeholder="Social post link or message (optional)"
            aria-label="Message"
            rows={3}
          />
          <div className="od-event-submit-grid">
            <input
              className="od-event-input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 120))}
              placeholder="Your name (optional)"
              aria-label="Your name"
            />
            <input
              className="od-event-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email (optional, for winner contact)"
              aria-label="Your email"
            />
          </div>

          <div className="od-event-submit-actions">
            <span className="od-event-submit-hint">Submit your social post proof for the contest.</span>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit entry'}
            </button>
          </div>
          {error && <p className="od-event-submit-error">{error}</p>}
        </form>
      )}
    </section>
  )
}
